import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Lock, Percent, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { CommissionSellerRow } from '@aeris/contracts'
import { ZERO_AMOUNT, amountToString, parseAmount } from '@aeris/finance'
import { add, formatMoney } from '@aeris/money'
import {
  commissionMonthReportFor,
  rateLabel,
  type PrintDocument,
} from '@aeris/printing'
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  LoadFailed,
  Page,
  PageHeader,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { CommissionCloseDialog } from '../components/commission-close-dialog.tsx'
import { CommissionRateDialog } from '../components/commission-rate-dialog.tsx'
import { CommissionSellerPanel } from '../components/commission-seller-panel.tsx'
import { ExportMenu } from '../components/export-menu.tsx'
import { PrintDocumentDialog } from '../components/print-document-dialog.tsx'
import {
  fetchCommissionOverview,
  fetchCommissionSeller,
  periodLabel,
  periodOf,
  periodShort,
  shiftPeriod,
} from '../lib/api/commissions.ts'
import { useAuth } from '../lib/auth-context.ts'
import { figuresOf } from '../lib/commission-figures.ts'
import {
  COMMISSION_EXPORT_COLUMNS,
  commissionExportRows,
  ratesLabel,
  statementInputOf,
} from '../lib/commission-report.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Comissões (F137).
 *
 * *"o admin conseguir metrificar a % de comissão de cada vendedor"*. Uma linha
 * por vendedor no mês: o que vendeu, o que voltou em troca, a base, a % e a
 * comissão — e se o mês já foi fechado e pago. Clicar abre as vendas que
 * compõem o número, porque "de onde saiu esse valor?" é a primeira pergunta de
 * quem recebe comissão.
 *
 * A tela abre no mês passado até o dia 10 — é quando se fecha a comissão — e
 * no mês atual depois disso.
 */

const money = (wire: string): string => formatMoney(parseAmount(wire))

const initialPeriod = (today: Date): string => {
  const current = periodOf(today)
  return today.getDate() <= 10 ? shiftPeriod(current, -1) : current
}

const SituationBadge = ({ row }: { row: CommissionSellerRow }): ReactNode => {
  const statement = row.statement
  if (statement === null) return <Badge tone="neutral">Prévia</Badge>
  if (statement.financeStatus === 'settled') {
    return <Badge tone="success">Pago</Badge>
  }
  return (
    <Badge tone="info" icon={<Lock aria-hidden="true" className="h-3 w-3" />}>
      Fechado · a pagar
    </Badge>
  )
}

export const ComissoesPage = (): ReactNode => {
  const { allows, session } = useAuth()
  const queryClient = useQueryClient()
  const workspace = useWorkspace()
  const search = useSearch({ strict: false })
  const canManage = allows('commission.manage')

  const today = new Date()
  const currentPeriod = periodOf(today)
  const [period, setPeriod] = useState(() => search.mes ?? initialPeriod(today))
  const [selected, setSelected] = useState<CommissionSellerRow | null>(null)
  const [closing, setClosing] = useState(false)
  // A % desde o mês da tela, direto do aviso "vendeu sem %" (F235).
  const [rateFor, setRateFor] = useState<{
    userId: string
    displayName: string
  } | null>(null)

  const overview = useQuery({
    queryKey: ['commissions', 'overview', period],
    queryFn: ({ signal }) => fetchCommissionOverview(period, signal),
  })

  const sellers = overview.data?.sellers ?? []
  const withoutRate = overview.data?.withoutRate ?? []
  const periodOver = overview.data?.periodOver ?? false

  const totals = sellers.reduce(
    (running, row) => {
      const figures = figuresOf(row)
      return {
        base: add(running.base, parseAmount(figures.baseAmount)),
        commission: add(
          running.commission,
          parseAmount(figures.commissionAmount)
        ),
      }
    },
    { base: ZERO_AMOUNT, commission: ZERO_AMOUNT }
  )
  const closedCount = sellers.filter((row) => row.statement !== null).length
  const closable = sellers.filter(
    (row) =>
      row.statement === null &&
      /^[0-9.]*[1-9]/.test(row.pending.commissionAmount) &&
      !row.pending.commissionAmount.startsWith('-')
  )

  /*
   * O relatório do mês de agora, sem precisar fechar — F160.
   *
   * O extrato de cada vendedor, com as peças, pelos MESMOS dados da gaveta (e
   * pela mesma chave de cache: quem já abriu a gaveta não baixa de novo).
   */
  const [monthReport, setMonthReport] = useState<PrintDocument | null>(null)
  const loadDetails = () =>
    Promise.all(
      sellers.map(async (row) => ({
        row,
        detail: await queryClient.fetchQuery({
          queryKey: ['commissions', 'seller', row.userId, period],
          queryFn: ({ signal }) =>
            fetchCommissionSeller(row.userId, period, signal),
        }),
      }))
    )
  const printMonth = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).
    meta: { errorShownInline: true },
    mutationFn: async () => {
      const details = await loadDetails()
      const companyName =
        session?.context?.companyTradeName ??
        session?.context?.companyName ??
        'Loja'
      return commissionMonthReportFor({
        company: { name: companyName },
        period: periodShort(period),
        sellers: details.map(({ row, detail }) => ({
          ...statementInputOf({
            companyName,
            period: periodShort(period),
            row,
            lines: detail.lines,
          }),
          rates: ratesLabel(row),
        })),
      })
    },
    onSuccess: setMonthReport,
  })

  const situation =
    sellers.length === 0
      ? '—'
      : closedCount === 0
        ? 'Prévia'
        : closable.length === 0
          ? 'Fechado'
          : 'Parcial'

  return (
    <Page>
      <PageHeader
        title="Comissões"
        description="A comissão de cada vendedor no mês: vendas, trocas, a % e o valor. Clique num vendedor para ver as vendas."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Mês anterior"
                leadingIcon={
                  <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setPeriod((value) => shiftPeriod(value, -1))
                }}
              />
              <span
                aria-live="polite"
                className="min-w-40 text-center text-(length:--text-sm) font-(--weight-semibold) capitalize"
              >
                {periodLabel(period)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Próximo mês"
                disabled={period >= currentPeriod}
                leadingIcon={
                  <ChevronRight aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setPeriod((value) => shiftPeriod(value, 1))
                }}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
              disabled={sellers.length === 0}
              loading={printMonth.isPending}
              onClick={() => {
                printMonth.mutate()
              }}
            >
              Imprimir relatório
            </Button>
            <ExportMenu
              fileName={`comissoes-${period}`}
              sheetName="Comissões"
              columns={COMMISSION_EXPORT_COLUMNS}
              fetchPage={async () => {
                const rows = commissionExportRows(await loadDetails())
                return { items: rows, total: rows.length }
              }}
              pageSize={1_000_000}
              total={sellers.length}
            />
            {canManage ? (
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Lock aria-hidden="true" className="h-4 w-4" />}
                disabled={overview.data === undefined}
                onClick={() => {
                  setClosing(true)
                }}
              >
                Fechar mês
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          label="Base do mês"
          loading={overview.isPending}
          error={overview.isError}
          value={formatMoney(totals.base)}
        />
        <StatTile
          label="Comissão"
          loading={overview.isPending}
          error={overview.isError}
          value={formatMoney(totals.commission)}
        />
        <StatTile
          label="Vendedores"
          loading={overview.isPending}
          error={overview.isError}
          value={String(sellers.length)}
        />
        <StatTile
          label="Situação"
          loading={overview.isPending}
          error={overview.isError}
          value={situation}
        />
      </div>

      {withoutRate.length === 0 ? null : (
        <InlineAlert
          tone="warning"
          title={
            withoutRate.length === 1
              ? '1 pessoa vendeu sem % de comissão definida'
              : `${String(withoutRate.length)} pessoas venderam sem % de comissão definida`
          }
        >
          <span className="flex flex-wrap items-center gap-2">
            {withoutRate
              .map(
                (row) =>
                  `${row.name} (${money(row.salesAmount)} em ${String(row.salesCount)} ${row.salesCount === 1 ? 'venda' : 'vendas'})`
              )
              .join(', ')}
            . A % só conta nas vendas feitas depois de valer — para estas,
            defina a % desde 1º de {periodLabel(period)}.
            {canManage ? (
              withoutRate.map((row) => (
                <Button
                  key={row.userId}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setRateFor({ userId: row.userId, displayName: row.name })
                  }}
                >
                  {`Definir % de ${row.name}`}
                </Button>
              ))
            ) : allows('identity.user.read') ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  workspace.open({ module: 'equipe' })
                }}
              >
                Definir em Equipe
              </Button>
            ) : null}
          </span>
        </InlineAlert>
      )}

      {rateFor === null ? null : (
        <CommissionRateDialog
          member={rateFor}
          initialSince={period}
          onClose={() => {
            setRateFor(null)
          }}
        />
      )}

      {overview.isError ? (
        <LoadFailed
          what="as comissões"
          onRetry={() => {
            void overview.refetch()
          }}
        />
      ) : overview.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : sellers.length === 0 ? (
        <EmptyState
          icon={<Percent aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma comissão neste mês"
          description="Defina a % de cada vendedor em Configurações → Equipe → Comissão. A partir daí, cada venda concluída entra aqui."
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Vendedor</TableHeaderCell>
                <TableHeaderCell numeric>Vendas</TableHeaderCell>
                <TableHeaderCell numeric>Vales e canceladas</TableHeaderCell>
                <TableHeaderCell numeric>Base</TableHeaderCell>
                <TableHeaderCell numeric>%</TableHeaderCell>
                <TableHeaderCell numeric>Comissão</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sellers.map((row) => {
                const figures = figuresOf(row)
                const hasReturns = /[1-9]/.test(
                  amountToString(figures.returnsAmount)
                )
                return (
                  <TableRow
                    key={row.userId}
                    className="cursor-pointer hover:bg-(--surface-hover)"
                    onClick={() => {
                      setSelected(row)
                    }}
                  >
                    <TableCell>
                      <button
                        type="button"
                        className="flex items-center gap-2 text-left"
                        onClick={(event) => {
                          event.stopPropagation()
                          setSelected(row)
                        }}
                      >
                        <Avatar label={row.name} seed={row.userId} />
                        <span className="flex flex-col">
                          <span className="font-(--weight-medium) text-(--text-primary)">
                            {row.name}
                          </span>
                          <span className="text-(length:--text-xs) text-(--text-muted)">
                            {row.payee === null
                              ? 'sem quem receba'
                              : `recebe: ${row.payee.name}`}
                          </span>
                        </span>
                      </button>
                    </TableCell>
                    <TableCell numeric>
                      {money(figures.salesAmount)}
                      {figures.salesCount === null ? null : (
                        <span className="block text-(length:--text-xs) text-(--text-muted)">
                          {`${String(figures.salesCount)} ${figures.salesCount === 1 ? 'venda' : 'vendas'}`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell numeric>
                      {hasReturns ? (
                        <span className="text-(--danger)">
                          {`− ${formatMoney(figures.returnsAmount)}`}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell numeric>{money(figures.baseAmount)}</TableCell>
                    <TableCell numeric>
                      {figures.rates.length === 0
                        ? row.currentRate === null
                          ? '—'
                          : rateLabel(row.currentRate)
                        : figures.rates.map(rateLabel).join(' e ')}
                    </TableCell>
                    <TableCell numeric>
                      <strong>{money(figures.commissionAmount)}</strong>
                      {row.statement !== null &&
                      /[1-9]/.test(row.pending.commissionAmount) ? (
                        <span className="block text-(length:--text-xs) text-(--text-muted)">
                          {`+ ${money(row.pending.commissionAmount)} para o próximo fechamento`}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <SituationBadge row={row} />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selected === null ? null : (
        <CommissionSellerPanel
          period={period}
          row={
            sellers.find((row) => row.userId === selected.userId) ?? selected
          }
          canManage={canManage}
          onClose={() => {
            setSelected(null)
          }}
        />
      )}

      {printMonth.isError ? (
        <InlineAlert tone="danger">
          Não foi possível montar o relatório do mês. Tente de novo.
        </InlineAlert>
      ) : null}

      {monthReport === null ? null : (
        <PrintDocumentDialog
          document={monthReport}
          description={`Comissões de ${periodLabel(period)}`}
          initialPaper="a4"
          onClose={() => {
            setMonthReport(null)
          }}
        />
      )}

      {closing && overview.data !== undefined ? (
        <CommissionCloseDialog
          period={period}
          periodOver={periodOver}
          sellers={sellers}
          onClose={() => {
            setClosing(false)
          }}
        />
      ) : null}
    </Page>
  )
}
